import { RouteConfig } from 'vue-router';

import { PREFIX_IN_BROWSER_TAB } from '@/common/platform-configuration/global-variables';
import { guards } from '@/main/multipleGuardsHandler';
import { checkIfAvailableByLaunchDarkly } from '@/launch-darkly/routes/launch-darkly.guard';
import PlanFeaturesList from '@/plan-features/plan-features-list';
import { checkIfRouteAvailableByPlanFeatures } from '@/plan-features/routes';
import { AppFeaturesList } from '@/launch-darkly/models';
import { redirectIfGuard } from '@/common/services/redirectIf.guard';
import store, { smartRootModule } from '@/main/baseStore';
import { Permissions } from '@/permissions';

export const enum Paths {
    REPORTING = 'reporting',
    COURSE = 'course',
    USER = 'user',
    REPORT = 'report',
    ADMIN_REPORT = 'admin-report',
    ALL_COURSES = 'courses',
    ALL_USERS = 'users',
    SEGMENTS = 'segments',
    GROUP = 'group',
    SEGMENT_OVERVIEW = 'segment-overview',
    TOPIC = 'topic',
    MOBILE = 'mobile',
    SKILLS = 'skills',
}

export enum Names {
    ROOT_REPORT = 'root-report',
    COURSE_REPORT = 'course-report',
    USER_REPORT = 'user-report',
    MY_REPORT = 'my-report',
    ROOT_ADMIN_REPORT = 'admin-report-root',
    MY_ADMIN_REPORT = 'my-admin-report',
    USERS_REPORT = 'admin-users-report',
    COURSES_REPORT = 'admin-courses-report',
    SEGMENT_GROUP_REPORT = 'segment-group-report',
    SEGMENT_OVERVIEW = 'segment-overview',
    SEGMENT_TOPIC_REPORT = 'admin-topic-report',
    GROUPS_REPORT = 'group-report',
    MY_GROUP_REPORT = 'my-group-report',
    MOBILE_ROOT = 'mobile-reporting',
    REPORT_MANAGER_MOBILE_ROOT = 'report-manager-mobile-reporting',
    SKILLS_REPORT = 'skills-report',
    SKILL_REPORT = 'skill-report',
}

const COURSE_REPORT_PAGE = () => import('./pages/CourseReportPage.vue');
const USER_REPORT_PAGE = () => import('./pages/UserReportPage.vue');
const MY_REPORT = () => import('./components/MyReport.vue');
const ALL_COURSES_REPORT_PAGE = () => import('./pages/AllCoursesReportPage.vue');
const ALL_USERS_REPORT_PAGE = () => import('./pages/AllUsersReportPage.vue');
const SEGMENT_GROUP_REPORT_PAGE = () => import('./pages/SegmentGroupReportPage.vue');
const SEGMENT_OVERVIEW_PAGE = () => import('./pages/SegmentOverviewPage.vue');
const SEGMENT_TOPIC_REPORT_PAGE = () => import('./pages/SegmentTopicReportPage.vue');
const GROUP_REPORT_PAGE = () => import('./pages/GroupReportPage.vue');
const ADMIN_ROOT_PAGE = () => import('@/admin/pages/AdminRootPage.vue');
const MOBILE_ROOT_PAGE = () => import('@/reporting/pages/ReportingRootPage.vue');
const SKILLS_REPORT_PAGE = () => import('@/reporting/pages/SkillsReportPage.vue');
const SKILL_REPORT_PAGE = () => import('@/reporting/pages/SkillReportPage.vue');

const segmentReportingGuard = guards([
    checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.SEGMENT_REPORTING),
    checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.REPORTING),
]);

const routes: RouteConfig[] = [
    {
        path: `${Paths.REPORT}`,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Report`,
            showNavigationSidebar: true,
            leftSidebarIsCompact: true,
        },
        beforeEnter: checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.REPORTING),
        children: [
            {
                path: ``,
                name: Names.MY_REPORT,
                meta: {
                    title: `${PREFIX_IN_BROWSER_TAB} | Report`,
                    showNavigationSidebar: true,
                    leftSidebarIsCompact: true,
                    titleTranslationKey: 'spa.navigation.sections.reports.my-learning',
                },
                component: MY_REPORT,
            },
            {
                path: `${Paths.GROUP}`,
                name: Names.MY_GROUP_REPORT,
                meta: {
                    title: `${PREFIX_IN_BROWSER_TAB} | Report`,
                    showNavigationSidebar: true,
                    leftSidebarIsCompact: true,
                    titleTranslationKey: 'spa.navigation.sections.reports.groups-report',
                },
                component: GROUP_REPORT_PAGE,
            },
            {
                path: `${Paths.MOBILE}`,
                name: Names.REPORT_MANAGER_MOBILE_ROOT,
                meta: {
                    title: `${PREFIX_IN_BROWSER_TAB} | Reporting`,
                    titleTranslationKey: 'spa.navigation.sections.main.reports',
                },
                component: MOBILE_ROOT_PAGE,
                beforeEnter: guards([redirectIfGuard(() => isDesktop(), Names.MY_REPORT)]),
            },
        ],
        component: ADMIN_ROOT_PAGE,
    },
    {
        path: `${Paths.REPORTING}/${Paths.COURSE}/:course_id(\\d+)`,
        name: Names.COURSE_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Reporting`,
            defaultBackRoute: Names.COURSES_REPORT,
            requiredPermissions: [Permissions.Reporting],
        },
        component: COURSE_REPORT_PAGE,
    },
    {
        path: `${Paths.REPORTING}/${Paths.USER}/:user_id(\\d+)`,
        name: Names.USER_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Reporting`,
            defaultBackRoute: Names.USERS_REPORT,
            requiredPermissions: [],
        },
        component: USER_REPORT_PAGE,
    },
    {
        path: `${Paths.REPORTING}/${Paths.SEGMENTS}/${Paths.GROUP}/:id`,
        name: Names.SEGMENT_GROUP_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Segment Group Report`,
            defaultBackRoute: Names.SEGMENT_OVERVIEW,
            requiredPermissions: [],
        },
        beforeEnter: segmentReportingGuard,
        component: SEGMENT_GROUP_REPORT_PAGE,
    },
    {
        path: `${Paths.REPORTING}/${Paths.SEGMENTS}/${Paths.TOPIC}/:id`,
        name: Names.SEGMENT_TOPIC_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Segment Topic Report`,
            defaultBackRoute: Names.SEGMENT_OVERVIEW,
            requiredPermissions: [Permissions.Reporting],
        },
        beforeEnter: segmentReportingGuard,
        component: SEGMENT_TOPIC_REPORT_PAGE,
    },
    {
        path: `${Paths.REPORTING}/${Paths.SKILLS}/:id`,
        name: Names.SKILL_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Skill Report`,
            defaultBackRoute: Names.SKILLS_REPORT,
            requiredPermissions: [Permissions.Reporting, Permissions.AdminSkills],
        },
        beforeEnter: guards([checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.SKILLS)]),
        component: SKILL_REPORT_PAGE,
    },
];

export const adminReportRoutes: RouteConfig[] = [
    {
        path: `${Paths.REPORT}/`,
        name: Names.MY_ADMIN_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Report`,
            showNavigationSidebar: true,
            leftSidebarIsCompact: true,
            titleTranslationKey: 'spa.navigation.sections.reports.my-learning',
            requiredPermissions: [Permissions.Reporting],
        },
        component: MY_REPORT,
        beforeEnter: checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.REPORTING),
    },
    {
        path: `${Paths.REPORT}/${Paths.ALL_COURSES}/`,
        name: Names.COURSES_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Courses Report`,
            showNavigationSidebar: true,
            leftSidebarIsCompact: true,
            titleTranslationKey: 'spa.navigation.sections.reports.courses',
            requiredPermissions: [Permissions.Reporting],
        },
        component: ALL_COURSES_REPORT_PAGE,
        beforeEnter: checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.REPORTING),
    },
    {
        path: `${Paths.REPORT}/${Paths.ALL_USERS}/`,
        name: Names.USERS_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Users Report`,
            showNavigationSidebar: true,
            leftSidebarIsCompact: true,
            titleTranslationKey: 'spa.navigation.sections.reports.users',
            requiredPermissions: [Permissions.Reporting],
        },
        component: ALL_USERS_REPORT_PAGE,
        beforeEnter: checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.REPORTING),
    },
    {
        path: `${Paths.REPORT}/${Paths.SEGMENT_OVERVIEW}/`,
        name: Names.SEGMENT_OVERVIEW,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Users Report`,
            showNavigationSidebar: true,
            leftSidebarIsCompact: true,
            titleTranslationKey: 'spa.navigation.sections.reports.segments',
            requiredPermissions: [Permissions.Reporting],
        },
        component: SEGMENT_OVERVIEW_PAGE,
        beforeEnter: segmentReportingGuard,
    },
    {
        path: `${Paths.REPORT}/${Paths.GROUP}/`,
        name: Names.GROUPS_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Groups Report`,
            showNavigationSidebar: true,
            leftSidebarIsCompact: true,
            titleTranslationKey: 'spa.navigation.sections.reports.groups',
            requiredPermissions: [Permissions.Reporting],
        },
        component: GROUP_REPORT_PAGE,
        beforeEnter: guards([
            checkIfAvailableByLaunchDarkly(AppFeaturesList.GROUPS_REPORTING_MANAGER),
            checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.USER_PERMISSIONS),
            checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.REPORTING),
        ]),
    },
    {
        path: `${Paths.REPORT}/${Paths.SKILLS}/`,
        name: Names.SKILLS_REPORT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Skills Report`,
            showNavigationSidebar: true,
            leftSidebarIsCompact: true,
            titleTranslationKey: 'spa.navigation.sections.reports.skills',
            requiredPermissions: [Permissions.Reporting],
        },
        component: SKILLS_REPORT_PAGE,
        beforeEnter: guards([checkIfRouteAvailableByPlanFeatures(PlanFeaturesList.SKILLS)]),
    },
    {
        path: `${Paths.REPORT}/${Paths.MOBILE}`,
        name: Names.MOBILE_ROOT,
        meta: {
            title: `${PREFIX_IN_BROWSER_TAB} | Reporting`,
            titleTranslationKey: 'spa.navigation.sections.main.reports',
            requiredPermissions: [Permissions.Reporting],
        },
        component: MOBILE_ROOT_PAGE,
        beforeEnter: guards([redirectIfGuard(() => isDesktop(), Names.MY_ADMIN_REPORT)]),
    },
];

const isDesktop = () => {
    const module = smartRootModule.context(store).modules.mobileMode;

    return Boolean(!module.getters.isMobile());
};

export default routes;
